import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import BookingDetailModal from "./BookingDetailModal";

const MONTHS_PT = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const WEEKDAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

const HOUSE_PALETTE = [
  "#C5A059", // gold
  "#0A1128", // dark blue
  "#2D6A4F", // emerald
  "#9B2226", // deep red
  "#3A435E", // slate
  "#8C6B3A", // bronze
  "#5C80BC", // dusty blue
  "#A4786C", // warm taupe
];

const parseD = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const sameMonth = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

// Return ISO week grid (Monday-first) of 42 days for the given month
const buildGrid = (year, month) => {
  const first = new Date(year, month, 1);
  const start = new Date(first);
  const offset = (first.getDay() + 6) % 7; // Monday=0
  start.setDate(first.getDate() - offset);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
};

const BookingCalendar = ({ houses = [], bookings = [], onBookingChanged }) => {
  const now = new Date();
  const [cursor, setCursor] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const [selectedHouse, setSelectedHouse] = useState("all");
  const [selectedBooking, setSelectedBooking] = useState(null);

  const houseColor = useMemo(() => {
    const m = {};
    houses.forEach((h, i) => {
      m[h.id] = HOUSE_PALETTE[i % HOUSE_PALETTE.length];
    });
    return m;
  }, [houses]);

  const prev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const next = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const today = () => setCursor(new Date(now.getFullYear(), now.getMonth(), 1));

  const grid = useMemo(() => buildGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);

  const filteredBookings = useMemo(() => {
    return bookings.filter((b) => selectedHouse === "all" || b.house_id === selectedHouse);
  }, [bookings, selectedHouse]);

  const bookingsByDay = useMemo(() => {
    const map = new Map();
    filteredBookings.forEach((b) => {
      const ci = parseD(b.check_in);
      const co = parseD(b.check_out);
      for (let d = new Date(ci); d < co; d.setDate(d.getDate() + 1)) {
        const key = d.toISOString().slice(0, 10);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(b);
      }
    });
    return map;
  }, [filteredBookings]);

  const monthLabel = `${MONTHS_PT[cursor.getMonth()]} ${cursor.getFullYear()}`;
  const monthBookings = filteredBookings.filter((b) => {
    const ci = parseD(b.check_in);
    const co = parseD(b.check_out);
    return (
      (ci.getFullYear() === cursor.getFullYear() && ci.getMonth() === cursor.getMonth()) ||
      (co.getFullYear() === cursor.getFullYear() && co.getMonth() === cursor.getMonth()) ||
      (ci < new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1) &&
        co > new Date(cursor.getFullYear(), cursor.getMonth(), 1))
    );
  });
  const monthRevenue = monthBookings.reduce((s, b) => s + (b.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="booking-calendar">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline flex items-center gap-2">
            <CalendarDays size={14} strokeWidth={1.4} /> Calendário de reservas
          </div>
          <h3 className="font-serif-display text-3xl md:text-5xl font-light text-[#0A1128] capitalize mt-2">
            {monthLabel}
          </h3>
          <p className="text-sm text-[#3A435E] mt-1">
            {monthBookings.length} reserva(s) · {new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(monthRevenue)} de receita prevista
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedHouse}
            onChange={(e) => setSelectedHouse(e.target.value)}
            className="bg-[#FCFAF8] border border-[#0A1128]/15 px-3 py-2 text-sm text-[#0A1128]"
            data-testid="calendar-house-filter"
          >
            <option value="all">Todas as casas</option>
            {houses.map((h) => (
              <option key={h.id} value={h.id}>{h.name}</option>
            ))}
          </select>
          <button
            onClick={today}
            className="border border-[#0A1128]/20 px-4 py-2 text-xs tracking-[0.2em] uppercase hover:bg-[#0A1128] hover:text-white transition-colors"
            data-testid="calendar-today"
          >
            Hoje
          </button>
          <button onClick={prev} className="w-10 h-10 border border-[#0A1128]/20 grid place-items-center hover:bg-[#0A1128] hover:text-white transition-colors" data-testid="calendar-prev" aria-label="Mês anterior">
            <ChevronLeft size={16} strokeWidth={1.4} />
          </button>
          <button onClick={next} className="w-10 h-10 border border-[#0A1128]/20 grid place-items-center hover:bg-[#0A1128] hover:text-white transition-colors" data-testid="calendar-next" aria-label="Mês seguinte">
            <ChevronRight size={16} strokeWidth={1.4} />
          </button>
        </div>
      </div>

      {/* Legend */}
      {houses.length > 0 && selectedHouse === "all" && (
        <div className="flex flex-wrap gap-3 text-xs">
          {houses.map((h) => (
            <span key={h.id} className="flex items-center gap-2 text-[#3A435E]">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: houseColor[h.id] }} />
              {h.name}
            </span>
          ))}
        </div>
      )}

      {/* Grid */}
      <div className="bg-[#FCFAF8] border border-[#0A1128]/10">
        <div className="grid grid-cols-7 border-b border-[#0A1128]/10">
          {WEEKDAYS.map((w) => (
            <div key={w} className="px-2 py-3 text-[10px] tracking-[0.2em] uppercase text-[#3A435E] text-center">{w}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((d, i) => {
            const inMonth = sameMonth(d, cursor);
            const key = d.toISOString().slice(0, 10);
            const dayBookings = bookingsByDay.get(key) || [];
            const isToday =
              d.getFullYear() === now.getFullYear() &&
              d.getMonth() === now.getMonth() &&
              d.getDate() === now.getDate();
            return (
              <div
                key={key}
                className={`min-h-[96px] md:min-h-[112px] border-b border-r border-[#0A1128]/10 p-2 ${inMonth ? "bg-[#FCFAF8]" : "bg-[#F4F1EB]/40"}`}
                data-testid={`calendar-cell-${key}`}
              >
                <div className={`text-xs ${inMonth ? "text-[#0A1128]" : "text-[#0A1128]/30"} ${isToday ? "font-semibold text-[#C5A059]" : ""}`}>
                  {d.getDate()}
                </div>
                <div className="mt-1 space-y-1">
                  {dayBookings.slice(0, 3).map((b, bi) => {
                    const ci = parseD(b.check_in);
                    const isStart =
                      d.getFullYear() === ci.getFullYear() &&
                      d.getMonth() === ci.getMonth() &&
                      d.getDate() === ci.getDate();
                    return (
                      <button
                        type="button"
                        key={b.id + bi}
                        onClick={() => setSelectedBooking(b)}
                        className="block w-full text-left text-[10px] leading-tight px-1.5 py-1 text-white truncate hover:brightness-110 hover:ring-1 hover:ring-[#C5A059] transition-all cursor-pointer"
                        style={{ background: houseColor[b.house_id] || "#3A435E" }}
                        title={`${b.guest_name} · ${b.check_in} → ${b.check_out} · €${Math.round(b.amount || 0)} — clique para editar`}
                        data-testid={`calendar-booking-${b.id}`}
                      >
                        {isStart ? "→ " : ""}{b.guest_name}
                      </button>
                    );
                  })}
                  {dayBookings.length > 3 && (
                    <div className="text-[10px] text-[#3A435E]">+{dayBookings.length - 3}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <BookingDetailModal
        booking={selectedBooking}
        houses={houses}
        open={!!selectedBooking}
        onClose={() => setSelectedBooking(null)}
        onChanged={() => onBookingChanged?.()}
      />
    </div>
  );
};

export default BookingCalendar;
